import React from 'react';
import { LISTED_GRAY, MUTED, GREEN_ON_WHITE } from '../proUi';

/**
 * Vendor-page primitives (docs/plans/2026-09-vendor-pages). Composes with proUi.jsx.
 *
 * GLYPH SUBSTITUTION — the drag handle is a fifth inline SVG beyond the system's four
 * (chevron, X, check, plus/minus): Lucide grip-horizontal drawn as three strokes,
 * stroke-width 2, square caps, miter joins, currentColor. It is the only glyph added by
 * the whole vendor programme (Vendor Setup By Kind, appendix C).
 */
export function GripGlyph({ size = 'row', className = '' }) {
  const dims = { row: [10, 12, ['M1 3h8', 'M1 6h8', 'M1 9h8']], band: [12, 12, ['M1 3h10', 'M1 6h10', 'M1 9h10']], card: [12, 14, ['M1 3h10', 'M1 7h10', 'M1 11h10']] }[size] || [10, 12, ['M1 3h8', 'M1 6h8', 'M1 9h8']];
  return (
    <svg width={dims[0]} height={dims[1]} viewBox={`0 0 ${dims[0]} ${dims[1]}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true" className={`flex-none ${className}`}>
      {dims[2].map((d) => <path key={d} d={d} />)}
    </svg>
  );
}

export const KIND_LABEL = { bookable: 'I GET BOOKED', eatery: 'PEOPLE EAT HERE', bar: 'PEOPLE DRINK HERE', venue: 'PEOPLE PARTY HERE', shop: 'I SELL OR FIX THINGS' };
export const KINDS = [
  // Every kind unlocks EVENTS — any event can be linked to any page from the dashboard.
  { key: 'bookable', label: 'I GET BOOKED', blurb: 'You turn up and do a job — a DJ, a caterer, a security crew.', blocks: ['SERVICES', 'AVAILABILITY', 'EVENTS', 'FACES'] },
  { key: 'eatery', label: 'PEOPLE EAT HERE', blurb: 'A restaurant, café or food hall with a room and a kitchen.', blocks: ['MENU', 'EVENTS', 'HOURS', 'WHERE', 'ATTRIBUTES'] },
  { key: 'bar', label: 'PEOPLE DRINK HERE', blurb: 'A bar, pub, brewery or lounge. Taps, cocktails, a happy hour.', blocks: ['DRINKS', 'SNACKS', 'HAPPY HOUR', 'WHAT’S ON', 'EVENTS'] },
  { key: 'venue', label: 'PEOPLE PARTY HERE', blurb: 'A nightclub, live room or hall. You sell nights and hire the room out.', blocks: ['EVENTS', 'HIRE', 'HOURS', 'WHERE'] },
  { key: 'shop', label: 'I SELL OR FIX THINGS', blurb: 'A storefront — tires, haircuts, printing, anything with a counter.', blocks: ['SERVICES', 'BRANDS', 'EVENTS', 'HOURS', 'WHERE'] },
];
export const isVendorKind = (k) => k && k !== 'bookable';
export const MARKS = ['V', 'VG', 'GF', 'N'];
export const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
/** MON→SUN display order as DayOfWeek numbers. */
export const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** Dietary mark — a letter in a 2px-bordered square. Never an icon, never coloured. */
export function DietaryMark({ label, size = 22, on = true, onClick, fontSize }) {
  const color = on ? '#000' : LISTED_GRAY;
  const cls = 'inline-flex flex-none items-center justify-center font-mono font-bold';
  const style = { width: size, height: size, fontSize: fontSize || (size >= 40 ? 13 : 10), letterSpacing: '0.02em', border: `2px solid ${color}`, color: on && onClick ? '#fff' : color, background: on && onClick ? '#000' : '#fff', boxSizing: 'border-box' };
  if (onClick) return <button type="button" onClick={onClick} aria-pressed={on} aria-label={`Mark ${label}`} className={cls} style={style}>{label}</button>;
  return <span className={cls} style={style} aria-label={`Mark ${label}`}>{label}</span>;
}

/** Inline field in a grid row — the dense admin input: 12px, 2px black border, no label element. */
export function InlineInput({ className = '', style, ...rest }) {
  return <input className={`pr-pill-input font-mono text-black ${className}`} style={{ fontSize: 12, height: 40, padding: '0 14px', minWidth: 0, border: 'none', borderRadius: 999, background: '#F5F5F5', boxSizing: 'border-box', ...style }} {...rest} />;
}

/** NEW control (flagged): the lighter 11px note field stacked under a row's name. */
export function NoteField({ className = '', style, ...rest }) {
  return <input className={`pr-pill-input bg-white font-mono ${className}`} style={{ fontSize: 11, height: 34, padding: '0 14px', border: 'none', borderRadius: 999, boxShadow: 'inset 0 0 0 1.5px #EBEBEB', color: MUTED, minWidth: 0, boxSizing: 'border-box', ...style }} {...rest} />;
}

/** NEW control (flagged): 24×24 bordered square with the check glyph; the whole row is the target. */
export function CheckSquare({ on }) {
  return (
    <span className="flex flex-none items-center justify-center border-2 border-black" style={{ width: 24, height: 24, background: on ? '#000' : '#fff', marginTop: 1 }} aria-hidden="true">
      {on && <svg width="14" height="12" viewBox="0 0 14 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"><path d="M1 6 L5 10 L13 2" /></svg>}
    </span>
  );
}

/** Small action button in editors: 10px/700/0.05em, 2px black border, white. */
export function MiniButton({ fill = false, ghost = false, className = '', style, children, ...rest }) {
  return (
    <button type="button" className={`font-mono font-bold uppercase transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${fill ? 'bg-black text-white hover:bg-[#FF00AA]' : 'hover:bg-black hover:text-white'} ${className}`}
      style={{ fontSize: 10, letterSpacing: '0.12em', fontWeight: 800, height: 36, padding: '0 14px', border: 'none', borderRadius: 999, background: fill ? '#000' : '#F2F2F2', color: ghost ? MUTED : undefined, ...style }} {...rest}>
      {children}
    </button>
  );
}

/** Field label: 14px/600/uppercase/0.05em black (Input.jsx precedent). */
export function FieldLabel({ children, right, htmlFor }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label htmlFor={htmlFor} className="block font-mono font-semibold uppercase text-black" style={{ fontSize: 14, letterSpacing: '0.05em' }}>{children}</label>
      {right}
    </div>
  );
}

/** Labelled text input (Input.jsx recipe, without the password machinery). */
export function Field({ label, right, id, hint, textarea = false, className = '', style, ...rest }) {
  const Tag = textarea ? 'textarea' : 'input';
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && <FieldLabel htmlFor={id} right={right}>{label}</FieldLabel>}
      <Tag id={id} className="w-full border-2 border-black bg-white font-mono text-black outline-none placeholder:text-gray-400 focus:ring-2 focus:ring-black focus:ring-offset-2" style={{ fontSize: 13, padding: '9px 11px', lineHeight: textarea ? 1.5 : undefined, ...style }} {...rest} />
      {hint && <p className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: LISTED_GRAY }}>{hint}</p>}
    </div>
  );
}

/** Editor block: 2px black white card with a 12px/700/0.1em header band and an optional right slot. */
export function EditorBlock({ title, right, children, className = '' }) {
  return (
    <div className={`border-2 border-black bg-white ${className}`}>
      <div className="flex items-center justify-between gap-3 border-b-2 border-black" style={{ padding: '12px 16px' }}>
        <span className="font-mono font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.1em' }}>{title}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

/** Attribute / brand chip — the Listed Pros craft-chip pattern. */
export function Chip({ on, dashed = false, onClick, children, className = '' }) {
  const style = { fontSize: 11, letterSpacing: '0.05em', padding: '7px 10px', whiteSpace: 'nowrap', border: `2px ${dashed ? 'dashed' : 'solid'} ${on ? '#000' : LISTED_GRAY}`, background: on ? '#000' : '#fff', color: on ? '#fff' : MUTED };
  if (onClick) return <button type="button" onClick={onClick} aria-pressed={on} className={`font-mono font-bold uppercase transition-colors duration-200 ${className}`} style={style}>{children}</button>;
  return <span className={`font-mono font-bold uppercase ${className}`} style={style}>{children}</span>;
}

/** Status pill: 9px/700/0.1em, 2px border. */
export function Pill({ bg = '#fff', fg = '#000', border = '#000', children, className = '' }) {
  return <span className={`inline-block font-mono font-bold uppercase ${className}`} style={{ fontSize: 9, letterSpacing: '0.1em', padding: '4px 7px', border: `2px solid ${border}`, background: bg, color: fg, whiteSpace: 'nowrap' }}>{children}</span>;
}

// ── Formatting ──────────────────────────────────────────────────────────────

export const money = (cents, { whole = false } = {}) => {
  if (cents == null) return '';
  const v = cents / 100;
  return whole && Number.isInteger(v) ? `$${v.toLocaleString('en-US')}` : `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
export const priceLabel = (item) => (item.priceCents > 0 ? money(item.priceCents) : (item.priceText || ''));
export const durationLabel = (min) => {
  if (!min) return '';
  if (min % 60 === 0) { const h = min / 60; return `${h} HR${h === 1 ? '' : 'S'}`; }
  return `${min} MIN`;
};
export const monthDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase() : '');
export const dayMonthDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: '2-digit' }).toUpperCase() : '');
export const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');
export const sinceMonth = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short' }).toUpperCase() : '');
/** "1,2,3" or [1,2,3] → "MON — WED", "TUE · THU", "FRI". */
export const daysLabel = (days) => {
  const d = (Array.isArray(days) ? days : String(days || '').split(',').map(Number)).filter((x) => x >= 0 && x <= 6);
  if (d.length === 0) return '';
  const order = d.map((x) => DAY_ORDER.indexOf(x)).sort((a, b) => a - b);
  const contiguous = order.every((v, i) => i === 0 || v === order[i - 1] + 1);
  if (order.length >= 3 && contiguous) return `${DAYS[DAY_ORDER[order[0]]]} — ${DAYS[DAY_ORDER[order[order.length - 1]]]}`;
  return order.map((i) => DAYS[DAY_ORDER[i]]).join(' · ');
};
export const weeklyLabel = (days) => {
  const d = (Array.isArray(days) ? days : String(days || '').split(',').map(Number)).filter((x) => x >= 0 && x <= 6);
  const names = { 0: 'SUNDAYS', 1: 'MONDAYS', 2: 'TUESDAYS', 3: 'WEDNESDAYS', 4: 'THURSDAYS', 5: 'FRIDAYS', 6: 'SATURDAYS' };
  if (d.length === 7) return 'EVERY DAY';
  return d.map((x) => names[x]).join(' · ');
};
/** "17:00" → "17:00 — 22:00" row text or CLOSED. */
/** "23:00" → "11 PM", "17:30" → "5:30 PM", "11:00" → "11 AM". Human-readable 12-hour. */
export const clock12 = (t) => {
  const [h, m] = String(t || '').split(':').map(Number);
  if (!Number.isFinite(h)) return t || '';
  const ap = h % 24 < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${ap}` : `${hr} ${ap}`;
};
export const hoursRowText = (h) => (!h || h.isClosed || !h.opens || !h.closes ? 'CLOSED' : `${clock12(h.opens)} — ${clock12(h.closes)}`);

const toMin = (t) => { const [h, m] = String(t || '').split(':').map(Number); return Number.isFinite(h) ? h * 60 + (m || 0) : null; };

/**
 * Open-now on the viewer's clock (README §12). A close past midnight ("02:00") belongs to the
 * previous day's row. Returns { open, label, closesToday, dow } or null when there are no hours.
 */
export function openState(hours, now = new Date()) {
  if (!hours || hours.length === 0) return null;
  const byDay = Object.fromEntries(hours.map((h) => [h.dayOfWeek, h]));
  const dow = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  const spans = (d) => { const h = byDay[d]; if (!h || h.isClosed) return null; const o = toMin(h.opens); const c = toMin(h.closes); if (o == null || c == null) return null; return { o, c: c <= o ? c + 1440 : c, h }; };
  const today = spans(dow);
  if (today && mins >= today.o && mins < today.c) return { open: true, label: `OPEN NOW · CLOSES ${clock12(today.h.closes)}`, closesToday: today.h.closes, dow };
  const yesterday = spans((dow + 6) % 7);
  if (yesterday && yesterday.c > 1440 && mins < yesterday.c - 1440) return { open: true, label: `OPEN NOW · CLOSES ${clock12(yesterday.h.closes)}`, closesToday: yesterday.h.closes, dow };
  // Next opening.
  for (let i = 0; i < 7; i++) {
    const d = (dow + i) % 7; const s = spans(d);
    if (s && (i > 0 || mins < s.o)) return { open: false, label: `CLOSED · OPENS ${i === 0 ? '' : `${DAYS[d]} `}${clock12(s.h.opens)}`.replace('  ', ' '), closesToday: null, dow };
  }
  return { open: false, label: 'CLOSED', closesToday: null, dow };
}

export const attrLabel = (key) => ({
  takes_reservations: 'TAKES RESERVATIONS', walk_ins: 'WALK-INS', patio: 'PATIO', pickup: 'PICKUP', delivery: 'DELIVERY', dine_in: 'DINE-IN',
  wheelchair: 'WHEELCHAIR', private_dining: 'PRIVATE DINING', late_night: 'LATE NIGHT', nineteen_plus: '19+', dog_friendly: 'DOG-FRIENDLY',
  private_hire: 'PRIVATE HIRE', bottle_service: 'BOTTLE SERVICE', coat_check: 'COAT CHECK', in_house_sound: 'IN-HOUSE SOUND',
  appointments: 'APPOINTMENTS', parking: 'PARKING', drop_off: 'DROP-OFF', warranty: 'WARRANTY',
}[key] || String(key || '').replace(/_/g, ' ').toUpperCase());

export const ATTRIBUTE_SETS = {
  eatery: ['takes_reservations', 'walk_ins', 'patio', 'pickup', 'delivery', 'dine_in', 'wheelchair', 'private_dining'],
  bar: ['patio', 'late_night', 'nineteen_plus', 'dog_friendly', 'walk_ins', 'wheelchair', 'private_hire'],
  venue: ['nineteen_plus', 'late_night', 'bottle_service', 'coat_check', 'wheelchair', 'private_hire', 'in_house_sound'],
  shop: ['walk_ins', 'appointments', 'parking', 'wheelchair', 'drop_off', 'warranty'],
  bookable: [],
};

export const DEFAULT_SECTIONS = {
  bookable: ['SETS', 'ADD-ONS'], eatery: ['STARTERS', 'MAINS', 'DESSERT', 'DRINKS'], bar: ['ON TAP', 'COCKTAILS', 'WINE', 'SNACKS'], shop: ['SERVICES'], venue: [],
};
export const BAR_PRESETS = ['COCKTAILS', 'WINE BY THE GLASS', 'BOTTLES', 'NO ALCOHOL', 'COFFEE'];

/** The kind's craft line for the header: EATERY · ITALIAN · $$ */
export const craftLine = (biz) => {
  const kind = (biz.kind || 'bookable').toUpperCase();
  if (biz.kind === 'eatery') return [kind, biz.cuisine?.toUpperCase(), biz.priceBand ? '$'.repeat(biz.priceBand) : null].filter(Boolean).join(' · ');
  if (biz.kind === 'venue' || biz.kind === 'bar' || biz.kind === 'shop') return [kind, biz.cuisine?.toUpperCase() || (biz.craftLabels?.[0] || '').toUpperCase()].filter(Boolean).join(' · ');
  return null;
};

export { GREEN_ON_WHITE };
